// Sorting and pagination, in the two forms the interface needs.
//
// A listing that arrives complete — the departments of one company, the
// building blocks, a month of billing rows — sorts and pages in the browser.
// A listing that could be any size — people, companies, absences, jobs, the
// logs — asks the server for one page in one order, because sorting the two
// hundred rows that happen to be loaded is not sorting the list.
//
// Both are driven through the same object, so a table looks the same either
// way and switching one over later is a change of hook, not of markup.
import { useCallback, useEffect, useMemo, useState } from "react";

export type SortDir = "asc" | "desc";

export type Sort = { key: string; dir: SortDir } | null;

/** What a table component needs, whichever side does the work. */
export type TableState = {
  sort: Sort;
  /** Advance the given column: the first direction, the other, then off. */
  toggle: (key: string, first?: SortDir) => void;
  /** Set the ordering outright, for a control that is not a column header. */
  setSort: (sort: Sort) => void;
  offset: number;
  pageSize: number;
  setPageSize: (n: number) => void;
  total: number;
  /** Zero-based page index. */
  page: number;
  pageCount: number;
  goto: (page: number) => void;
};

export type ClientTable<T> = TableState & {
  /** The rows of the current page, in the chosen order. */
  rows: T[];
};

export const PAGE_SIZES = [10, 25, 50, 100, 250] as const;

// German collation, so that Ö sorts next to O and "Haus 2" before "Haus 10".
const collator = new Intl.Collator("de", { numeric: true, sensitivity: "base" });

/** How a value is read off a row when it is not simply row[key]. */
export type Accessors<T> = Record<string, (row: T) => unknown>;

function compare(a: unknown, b: unknown): number {
  // An empty cell sorts to the end in both directions, the way the server
  // orders NULLS LAST: turning the list around should not fill the first
  // screen with blanks.
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;

  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") {
    return a === b ? 0 : a ? 1 : -1;
  }
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return collator.compare(String(a), String(b));
}

function useSortAndPage(opts: {
  initial?: Sort;
  pageSize?: number;
  /** Values that, when they change, send the reader back to the first page. */
  resetOn?: unknown[];
}) {
  const [sort, setSort] = useState<Sort>(opts.initial ?? null);
  const [pageSize, setSizeRaw] = useState(opts.pageSize ?? 25);
  const [page, setPage] = useState(0);

  const resetKey = JSON.stringify(opts.resetOn ?? []);
  useEffect(() => {
    // A new search or filter is a new list; page four of the old one means
    // nothing in it.
    setPage(0);
  }, [resetKey]);

  const toggle = useCallback(
    (key: string, first: SortDir = "asc") => {
      setPage(0);
      setSort((current) => {
        if (current?.key !== key) return { key, dir: first };
        if (current.dir === first) return { key, dir: first === "asc" ? "desc" : "asc" };
        return null; // a third click restores the listing's own order
      });
    },
    [],
  );

  const choose = useCallback((next: Sort) => {
    setPage(0);
    setSort(next);
  }, []);

  const setPageSize = useCallback((n: number) => {
    setSizeRaw(n);
    setPage(0);
  }, []);

  return { sort, toggle, setSort: choose, page, setPage, pageSize, setPageSize };
}

/**
 * useClientTable sorts and pages a list that is already in hand.
 *
 * `accessors` is for the columns that are not a plain field: a count that is
 * computed, a name that comes from a lookup, a label built from two columns.
 */
export function useClientTable<T>(
  items: T[] | undefined,
  opts: {
    initial?: Sort;
    pageSize?: number;
    accessors?: Accessors<T>;
    resetOn?: unknown[];
  } = {},
): ClientTable<T> {
  const { sort, toggle, setSort, page, setPage, pageSize, setPageSize } =
    useSortAndPage(opts);
  const all = useMemo(() => items ?? [], [items]);
  const accessors = opts.accessors;

  const sorted = useMemo(() => {
    if (!sort) return all;
    const read =
      accessors?.[sort.key] ??
      ((row: T) => (row as Record<string, unknown>)[sort.key]);
    const sign = sort.dir === "asc" ? 1 : -1;

    // Sorting a copy: the array belongs to the query cache, and reordering it
    // in place would reorder it for everything else looking at the same data.
    return [...all].sort((a, b) => {
      const av = read(a);
      const bv = read(b);
      const aEmpty = av === null || av === undefined || av === "";
      const bEmpty = bv === null || bv === undefined || bv === "";
      // The empty ones stay at the end whichever way the rest is turned.
      if (aEmpty || bEmpty) return compare(av, bv);
      return sign * compare(av, bv);
    });
  }, [all, sort, accessors]);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // A list that shrinks — something deleted, a filter narrowed — must not
  // leave the reader on a page that no longer exists.
  const current = Math.min(page, pageCount - 1);
  const offset = current * pageSize;
  const rows = useMemo(
    () => sorted.slice(offset, offset + pageSize),
    [sorted, offset, pageSize],
  );

  return {
    rows,
    sort,
    toggle,
    setSort,
    offset,
    pageSize,
    setPageSize,
    total,
    page: current,
    pageCount,
    goto: setPage,
  };
}

/**
 * useServerTable keeps the same state for a listing the server pages.
 *
 * `query` is the part of the query string that says which page in which
 * order; put it in the react-query key as well, so that changing either
 * fetches rather than showing the previous page sorted the old way.
 */
export function useServerTable(opts: {
  total: number;
  initial?: Sort;
  pageSize?: number;
  resetOn?: unknown[];
}): TableState & { query: string } {
  const { sort, toggle, setSort, page, setPage, pageSize, setPageSize } = useSortAndPage({
    initial: opts.initial,
    pageSize: opts.pageSize ?? 50,
    resetOn: opts.resetOn,
  });

  const pageCount = Math.max(1, Math.ceil(opts.total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const offset = current * pageSize;

  const query = useMemo(() => {
    const p = new URLSearchParams({
      limit: String(pageSize),
      offset: String(offset),
    });
    if (sort) {
      p.set("sort", sort.key);
      p.set("dir", sort.dir);
    }
    return p.toString();
  }, [pageSize, offset, sort]);

  return {
    query,
    sort,
    toggle,
    setSort,
    offset,
    pageSize,
    setPageSize,
    total: opts.total,
    page: current,
    pageCount,
    goto: setPage,
  };
}
